<template>
  <div>
    <Button
      v-if="showButton"
      label="Add To Target List"
      fluid
      size="small"
      @click="toggleOpen"
    >
      <template #icon>
        <Icon name="material-symbols:target" />
      </template>
    </Button>

    <Dialog
      v-model:visible="dialog.visible"
      header="Add Contact To Target List"
      :style="{ width: '30rem' }"
      :modal="true"
    >
      <div
        class="flex flex-col gap-4"
      >
        <div class="w-full grid grid-cols-1 space-y-2">
          <label class="font-medium">
            Business
            <span class="text-red-500">*</span>
          </label>

          <Skeleton
            v-if="dialog.loadingBusinesses"
            height="2rem"
          />

          <Select
            v-else
            v-model="dialog.selectedBusiness"
            :options="businesses"
            optionLabel="name"
            placeholder="Select a business"
            fluid
            filter
            showClear
            size="small"
          />
        </div>
      </div>
      <template #footer>
        <div
          class="flex justify-end gap-2"
        >
          <Button
            outlined
            label="Cancel"
            :disabled="dialog.loading"
            size="small"
            icon="pi pi-times"
            @click="dialog.visible = false"
          />
          <Button
            label="Add"
            icon="pi pi-check"
            :loading="dialog.loading"
            size="small"
            @click="addToTargetList"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { Business, Contact } from '@prisma/client'

const toast = useToast()
const { createPageAccess } = usePageAccess()

const props = defineProps<{
  contact: Contact
}>()

const emit = defineEmits(['refresh'])

// Businesses
const { getAllBusinesses } = useBusiness()
const businesses = ref<Business[]>([])

const dialog = ref({
  visible: false,
  loading: false,
  loadingBusinesses: false,
  selectedBusiness: undefined as Business | undefined,
})

const showButton = defineModel<boolean>('showButton', {
  required: false,
  default: true,
})

const toggleOpen = async () => {
  dialog.value.visible = true
  dialog.value.loadingBusinesses = true

  const res = await getAllBusinesses()
  const businessesWithCIM = res.filter(business => business.Builder.some(builder => builder.builderType === 'CIM'))
  businesses.value = businessesWithCIM

  dialog.value.loadingBusinesses = false
}

defineExpose({
  toggleOpen,
})

const addToTargetList = async () => {
  if (!dialog.value.selectedBusiness) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please select a business',
      life: 3000,
    })
    dialog.value.loading = false
    throw Error('No business selected')
  }

  if (!props.contact) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Something went wrong',
      life: 3000,
    })
    dialog.value.loading = false
    throw Error('No contact found')
  }

  try {
    dialog.value.loading = true

    if (dialog.value.selectedBusiness && props.contact) {
      const pageAccessData = {
        businessId: dialog.value.selectedBusiness.id,
        firstName: props.contact.firstName,
        lastName: props.contact.lastName,
        email: props.contact.email,
        phone: props.contact.phone,
        tier: 'BASIC',
        status: 'TARGET_LIST',
        contactId: props.contact.id,
      }

      const { data, error } = await createPageAccess(pageAccessData)

      if (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.data.message || 'Error creating contact relationship',
          life: 3000,
        })
        dialog.value.selectedBusiness = undefined
        return
      }

      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Contact successfully added to the target list',
        life: 3000,
      })

      emit('refresh')
    }
  }
  catch (error) {
    console.error('Error adding contact to target list:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to add contact to the target list',
      life: 3000,
    })
  }
  finally {
    dialog.value.loading = false
    dialog.value.visible = false
  }
}
</script>
